// Horizontal row of reaction chips under a message.
//
// Presentational. All business logic lives in `useMessageActions`; this
// component knows how to render chips, how to anchor the picker, and
// nothing else. The picker itself is a child so callers don't have to
// thread `onPickEmoji` through every layer — a single `onToggle` call
// covers inactive chip clicks, active chip toggles, and picker picks.
//
// Tokens-only styling. Every dimension, colour, typography, and radius
// routes through the `--chat-*` custom properties so light/dark and
// future themes carry for free.

import {
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactElement,
} from "react";
import { Plus } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nurion/ui/tooltip";

import { EmojiPicker } from "~/components/EmojiPicker";
import { PersonAvatar } from "~/components/PersonAvatar";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import { IconTooltip } from "~/components/IconTooltip";
import { getCachedMember, getUserId, type Client, type ReactionSummary } from "~/lib/matrix";
import { matrixLocalpart } from "~/lib/display-name";
import { ICON_STROKE } from "~/lib/icon";

interface ReactionBarProps {
  reactions: ReactionSummary[];
  /**
   * Invoked when the user clicks a chip or picks an emoji from the
   * trailing "+" popover. The parent routes this to
   * `useMessageActions.toggleReaction(targetId, emoji, reactions)`.
   */
  onToggle: (emoji: string) => void | Promise<void>;
  /**
   * When false, every chip and the trailing "+" stop accepting input.
   * Parents use this to honour `useMessageActions.ready` so a click
   * before login completes doesn't throw through the hook.
   */
  disabled?: boolean;
  /** Storybook-only: force the picker open for a deterministic story. */
  initialPickerOpen?: boolean;
  client?: Client | null;
  roomId?: string | null;
}

const MAX_WHO_REACTED_ROWS = 8;

export function ReactionBar({
  reactions,
  onToggle,
  disabled = false,
  initialPickerOpen = false,
  client = null,
  roomId = null,
}: ReactionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(initialPickerOpen);
  const plusRef = useRef<HTMLButtonElement | null>(null);

  const hasChips = reactions.length > 0;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        rowGap: "var(--chat-space-1)",
        columnGap: "var(--chat-space-1)",
        marginTop: "var(--chat-space-1)",
        alignItems: "center",
      }}
    >
      {reactions.map((r) => (
        <WhoReactedTooltip key={r.emoji} reaction={r} client={client} roomId={roomId}>
          <ReactionChip reaction={r} onToggle={onToggle} disabled={disabled} />
        </WhoReactedTooltip>
      ))}
      <IconTooltip label={hasChips ? "Add reaction" : "React"}>
        <button
          ref={plusRef}
          type="button"
          className="chat-button"
          aria-label={hasChips ? "Add reaction" : "React"}
          aria-haspopup={true}
          aria-expanded={pickerOpen}
          disabled={disabled}
          onClick={() => setPickerOpen((open) => !open)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "var(--chat-chip-size)",
            height: "var(--chat-chip-size)",
            padding: 0,
            border: "1px solid transparent",
            borderRadius: "var(--chat-radius-full)",
            background: "var(--chat-surface-raised)",
            color: "var(--chat-fg-secondary)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Plus size={12} strokeWidth={ICON_STROKE} aria-hidden />
        </button>
      </IconTooltip>
      {pickerOpen && (
        <EmojiPicker
          anchor={plusRef.current}
          onSelect={(emoji) => {
            setPickerOpen(false);
            void onToggle(emoji);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function ReactionChip({
  reaction,
  onToggle,
  disabled,
  ...rest
}: {
  reaction: ReactionSummary;
  onToggle: (emoji: string) => void | Promise<void>;
  disabled: boolean;
} & Omit<ComponentProps<"button">, "onToggle" | "disabled">) {
  const active = reaction.reactedByMe;
  const chipStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    height: "var(--chat-chip-size)",
    padding: "0 var(--chat-space-2)",
    border: active ? "1px solid var(--chat-fg-muted)" : "1px solid transparent",
    borderRadius: "var(--chat-radius-full)",
    background: active ? "var(--chat-accent-muted)" : "var(--chat-surface-raised)",
    color: active ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-xs-size)",
    lineHeight: "var(--chat-type-body-xs-line)",
    fontWeight: 300,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
  return (
    <button
      {...rest}
      type="button"
      className="chat-button"
      aria-pressed={active}
      disabled={disabled}
      aria-label={
        active
          ? `Remove your ${reaction.emoji} reaction, ${reaction.count} total`
          : `React with ${reaction.emoji}, ${reaction.count} so far`
      }
      onClick={() => void onToggle(reaction.emoji)}
      style={chipStyle}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          fontSize: 16,
          width: 20,
          height: 16,
          marginRight: 4,
        }}
      >
        {reaction.emoji}
      </span>
      <span>{reaction.count}</span>
    </button>
  );
}

function WhoReactedTooltip({
  reaction,
  client,
  roomId,
  children,
}: {
  reaction: ReactionSummary;
  client: Client | null;
  roomId: string | null;
  children: ReactElement;
}): ReactElement {
  const portalContainer = usePortalRoot();
  const hasSenders = (reaction.senderIds?.length ?? 0) > 0;
  if (client === null || roomId === null || !hasSenders || portalContainer === null) {
    return <>{children}</>;
  }
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          portalContainer={portalContainer}
          side="top"
          sideOffset={6}
          collisionPadding={8}
        >
          <WhoReactedList reaction={reaction} client={client} roomId={roomId} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function WhoReactedList({
  reaction,
  client,
  roomId,
}: {
  reaction: ReactionSummary;
  client: Client;
  roomId: string;
}): ReactElement {
  const ids = reaction.senderIds ?? [];
  const visible = ids.slice(0, MAX_WHO_REACTED_ROWS);
  const remaining = Math.max(0, ids.length - MAX_WHO_REACTED_ROWS);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-1)",
      }}
    >
      {visible.map((id) => {
        const member = getCachedMember(client, roomId, id);
        const name = id === getUserId(client) ? "You" : member?.displayName || matrixLocalpart(id);
        return (
          <div
            key={id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-1)",
            }}
          >
            <PersonAvatar
              client={client}
              avatarUrl={member?.avatarUrl ?? null}
              name={name}
              size={20}
            />
            <span
              style={{
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              {name}
            </span>
          </div>
        );
      })}
      {remaining > 0 && (
        <div
          style={{
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
          }}
        >
          and {remaining} more
        </div>
      )}
    </div>
  );
}
